import * as microsoftTeams from "@microsoft/teams-js";

let teamsInitialized = false;
let isInTeams = false;
let teamsAuthInProgress = false;

export interface TeamsAuthResult {
  account: {
    homeAccountId: string;
    environment: string;
    tenantId: string;
    username: string;
    localAccountId: string;
    name?: string;
    idTokenClaims?: Record<string, unknown>;
  };
  /** Fernet-encrypted accessToken - unreadable without the server-side key */
  encryptedToken: string;
  /** Access token expiry as Unix timestamp in seconds (from AuthenticationResult.expiresOn) */
  accessTokenExpiry: number | null;
  scopes: string[];
}

/**
 * Initialize Teams SDK and detect if we're running in Teams.
 */
export async function initializeTeams(): Promise<boolean> {
  if (teamsInitialized) return isInTeams;

  // Outside Teams, the SDK waits ~5s for a parent handshake before rejecting. Cap that
  // wait at 2s so non-Teams iframes don't stall on auth init.
  const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), 2000));
  const teamsContext = (async () => {
    await microsoftTeams.app.initialize();
    return microsoftTeams.app.getContext();
  })().catch(() => null);

  const context = await Promise.race([teamsContext, timeout]);
  isInTeams = Boolean(context?.app?.host?.name);
  teamsInitialized = true;
  return isInTeams;
}

/**
 * Check if Teams auth is currently in progress
 */
export function isTeamsAuthInProgress(): boolean {
  return teamsAuthInProgress;
}

/**
 * Authenticate using Teams managed popup.
 * Opens a popup that redirects to B2C, then returns the auth result.
 */
export async function authenticateWithTeams(
  clientId: string,
  authority: string,
  apiScope: string
): Promise<TeamsAuthResult> {
  teamsAuthInProgress = true;  // Set flag before starting

  try {
    const authStartUrl = new URL(`${window.location.origin}/auth-start`);
    authStartUrl.searchParams.set("clientId", clientId);
    authStartUrl.searchParams.set("authority", authority);
    authStartUrl.searchParams.set("apiScope", apiScope);

    // The popup returns JSON with account info (plain) and a Fernet-encrypted token.
    // The encrypted token is unreadable to a malicious Teams opener.
    const resultString = await microsoftTeams.authentication.authenticate({
      url: authStartUrl.toString(),
      width: 600,
      height: 535,
      isExternal: false,
    });

    const authResult: TeamsAuthResult = JSON.parse(resultString);

    teamsAuthInProgress = false;  // Clear flag on success
    return authResult;
  } catch (error) {
    teamsAuthInProgress = false;  // Clear flag on error
    throw error;
  }
}